import clsx from 'clsx'

import { FvButton, Icon } from '@fv/client-components'
import {
  addressFuncs,
  composeStopDetails,
  displayLocShipType,
  getLocAccessorials,
} from '@fv/client-core'
import type { FullShipment } from '@fv/client-types'

import { useTypeNames } from '@/hooks/settings/useTypeNames'
import { getApplicableAccessorials } from '@/utils/locationFuncs'

import { List, ListItem } from '../List'
import { ShipperTooltip } from '../ShipperTooltip'

type Props = {
  editOptions?: ['all' | 'origin', (sequence: number) => void]
  numberOfTrucks: number
  load: FullShipment
}

export const RouteSummary = ({ editOptions, numberOfTrucks, load }: Props) => {
  const typeNames = useTypeNames()
  const { locations: stops, workflow, equipment } = load
  // ltl doesn't show equipment accessorials so we throw freezing into destination
  const protectFromFreezing =
    workflow === 'ltl' &&
    equipment.accessorials?.some(a => a.key === 'protect-from-freezing')
  const singleShipment =
    workflow === 'ltl' &&
    equipment.accessorials?.some(a => a.key === 'single-shipment')

  return (
    <List
      fcp0
      lcp0
      className="overflow-auto my-4 -mx-6 py-4 bg-white/70 border-t border-b border-fv-blue-150 px-6"
    >
      {numberOfTrucks > 1 && (
        <ListItem>
          <Icon icon="layer-group" className="text-fv-gray-dark" />
          <span>Multi-truck shipment ({numberOfTrucks})</span>
        </ListItem>
      )}

      {stops.length > 2 && (
        <ListItem>
          <Icon icon="route" className="text-fv-gray-dark" />
          <span>Multi-stop shipment</span>
        </ListItem>
      )}

      {stops.map(location => {
        const { sequence, stopDate, stopDateType, stopType } = location
        const shipType = displayLocShipType(location)
        const isFirstStop = sequence === 0
        const isLastStop = sequence === stops.length - 1
        const accessorials = (
          getLocAccessorials(
            { accessorials: getApplicableAccessorials(location, workflow) },
            typeNames,
          )?.map(a => a.name) ?? []
        ).concat(
          protectFromFreezing && isLastStop ? ['Protect from freezing'] : [],
          singleShipment && isLastStop ? ['Single shipment'] : [],
        )
        const address = addressFuncs.companyAddress(location)
        const canEditDate =
          !!editOptions && (editOptions[0] === 'all' || isFirstStop)

        return (
          <ListItem
            className="relative flex-wrap !py-4 first:!pt-1 last:!pb-1 !border-fv-blue-200"
            itemsStart
            key={sequence}
          >
            <div className="flex">
              <Icon
                className={clsx({
                  'text-fv-green': isFirstStop,
                  'text-fv-orange': isLastStop,
                  'text-fv-beer': !isFirstStop && !isLastStop,
                })}
                icon="map-marker"
                transform="down-2.5"
                fixedWidth
              />

              <p className="mb-0 leading-6 flex-1">
                {canEditDate && (
                  <FvButton
                    theme="underlined"
                    onClick={() => editOptions[1](sequence)}
                  >
                    {composeStopDetails({ stopDate, stopDateType, stopType })}
                  </FvButton>
                )}
                {!canEditDate &&
                  composeStopDetails({ stopDate, stopDateType, stopType })}{' '}
                at
                <br />
                <span>{` ${address}`}</span>
                {shipType && <span>{` / ${shipType}`}</span>}
                <span>
                  {accessorials.length > 0 &&
                    ` / Accessorials: ${accessorials.join(', ')}`}
                </span>
              </p>
            </div>
            <InternalNote
              internalNotes={location.internalNotes}
              isFirstStop={isFirstStop}
              isLastStop={isLastStop}
            />
          </ListItem>
        )
      })}
    </List>
  )
}

type InternalNoteProps = {
  internalNotes: string
  isFirstStop: boolean
  isLastStop: boolean
}
const InternalNote = ({
  internalNotes,
  isFirstStop,
  isLastStop,
}: InternalNoteProps) => {
  if (!internalNotes) {
    return null
  }
  return (
    <div className="flex border-t border-dashed border-fv-gray mt-2 pt-2">
      <Icon
        icon="clipboard"
        style={{
          '--fa-animation-duration': '2s',
          '--fa-animation-iteration-count': '5',
        }}
        className={clsx('fa-flip mt-1', {
          'text-fv-green': isFirstStop,
          'text-fv-orange': isLastStop,
          'text-fv-beer': !isFirstStop && !isLastStop,
        })}
        fixedWidth
      />

      <ShipperTooltip label={internalNotes}>
        <p
          className={clsx(
            'mb-0 line-clamp-2 leading-6 flex-1 underline underline-offset-4 ',
            {
              'decoration-fv-green-300': isFirstStop,
              'decoration-fv-orange-300': isLastStop,
              'decoration-fv-beer-medium': !isFirstStop && !isLastStop,
            },
          )}
        >
          {internalNotes}
        </p>
      </ShipperTooltip>
    </div>
  )
}
